// priceTab/view.js - Updated with expandable dynamic pricing section
import React from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  TextField,
  InputAdornment,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Slider,
  Switch,
  Divider,
  Alert,
  Card,
  CardContent,
  CardHeader,
  Collapse,
  Paper
} from '@mui/material';
import {
  AttachMoney as AttachMoneyIcon,
  Add as AddIcon,
  CurrencyPound as CurrencyPoundIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import {
  HoneyContainer,
  HoneyGalleryHeader,
  HoneyGalleryTitle,
  HoneyGallerySubtitle,
  StyledVATToggle,
  StyledTariffCard,
  StyledRuleBox,
  StyledNewRuleBox,
  StyledActionButtons,
  StyledBasePriceSection,
  StyledDayCheckboxGroup,
  StyledEmptyState,
  StyledLoadingContainer,
  StyledSaveButton,
  StyledAddRuleButton,
  StyledErrorAlert,
  StyledTariffActionButton,
  StyledDynamicPricingToggle,
  StyledDynamicPricingContainer
} from './styled';

const PricingTabView = ({
  // State
  tariffs,
  showNewRuleForm,
  isVatInclusive,
  isLoadingTariffs,
  tariffError,
  isSavingRule,
  newRules,
  tariffSavingState,
  editingPrices,
  dynamicPricingEnabled,
  
  // Handlers
  handleDayToggle,
  handleSliderChange,
  handleInputChange,
  handlePriceBlur,
  handleTariffPriceChange,
  handleTariffPriceBlur,
  handleNewRuleChange,
  handleNewRulePriceBlur,
  handleNewRuleDayToggle,
  handleSaveTariff,
  addTariffRule,
  handleTariffStatusToggle,
  handleTariffDateChange,
  toggleNewRuleForm,
  setIsVatInclusive,
  setTariffError,
  setDynamicPricingEnabled,
  handleDynamicPricingToggle,
  
  // Utils
  getDisplayPrice,
  getPriceKey,
  isDayIncluded,
  daysOfWeek,
  
  // Props
  VAT_RATE,
  isUpdating
}) => {
  const renderHeader = () => (
    <HoneyGalleryHeader>
      <HoneyGalleryTitle>
        <CurrencyPoundIcon />
        Pricing
      </HoneyGalleryTitle>
      <HoneyGallerySubtitle>
        Configure tariffs and pricing rules for this workspace
      </HoneyGallerySubtitle>
    </HoneyGalleryHeader>
  );

  const renderDiscreteVATToggle = () => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'flex-end',
      mt: 1
    }}>
      <FormControlLabel
        control={
          <Switch 
            checked={isVatInclusive} 
            onChange={(e) => setIsVatInclusive(e.target.checked)}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#F1C40F',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#D4A574',
              },
            }}
          />
        }
        label={
          <Typography variant="caption" sx={{ color: '#8B7355', fontSize: '0.75rem' }}>
            {isVatInclusive ? 'Show here the price without VAT' : 'Show here the price with VAT'}
          </Typography>
        }
        sx={{ 
          margin: 0,
          '& .MuiFormControlLabel-label': {
            fontSize: '0.75rem'
          }
        }}
      />
    </Box>
  );

  const renderDynamicPricingToggle = (tariff) => {
    // Check if any rules are active - this determines if toggle should be checked
    const hasActiveRules = tariff.rules && tariff.rules.length > 0 && 
      tariff.rules.some(rule => rule.status === 'AC');
    
    return (
      <Box sx={{ 
        backgroundColor: '#FFFEF7',
        borderRadius: '20px',
        padding: 2.5,
        marginBottom: 2,
        display: 'flex', 
        alignItems: 'center', 
        gap: 2 
      }}>
        <TrendingUpIcon sx={{ color: '#D4A574' }} />
        <FormControlLabel
          control={
            <Switch 
              checked={hasActiveRules}
              onChange={(e) => handleDynamicPricingToggle(tariff.slug, e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#F1C40F',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#D4A574',
                },
              }}
            />
          }
          label={
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#5D4E37' }}>
                Enable Dynamic Pricing
              </Typography>
              <Typography variant="body2" sx={{ color: '#8B7355', mt: 0.5 }}>
                Create special pricing rules based on occupancy and days of the week
              </Typography>
            </Box>
          }
        />
      </Box>
    );
  };

  const renderLoadingState = () => (
    <HoneyContainer>
      {renderHeader()}
      <StyledLoadingContainer>
        <CircularProgress size={32} />
        <Typography variant="body2" sx={{ mt: 1 }}>
          Loading tariff data...
        </Typography>
      </StyledLoadingContainer>
    </HoneyContainer>
  );

  const renderErrorAlert = () => (
    <StyledErrorAlert
      severity={tariffError?.includes('successfully') ? "success" : "error"}
      sx={{ mb: 2 }}
      onClose={() => setTariffError('')}
    >
      {tariffError}
    </StyledErrorAlert>
  );

  const renderEmptyState = () => (
    <HoneyContainer>
      {renderHeader()}
      <StyledEmptyState severity="info">
        No HD tariffs found for this work area.
      </StyledEmptyState>
    </HoneyContainer>
  );

  const renderDayCheckboxes = (selectedDays, onDayToggle) => (
    <StyledDayCheckboxGroup>
      {daysOfWeek.map((day, dayIndex) => (
        <FormControlLabel
          key={day}
          control={
            <Checkbox
              checked={isDayIncluded(selectedDays, dayIndex)}
              onChange={() => onDayToggle(dayIndex)}
              size="small"
            />
          }
          label={day}
        />
      ))}
    </StyledDayCheckboxGroup>
  );

  const renderOccupancySlider = (rule, onSliderChange) => (
    <Grid item xs={12} sm={8}>
      <Slider
        value={[
          parseFloat(rule.occupancy_min || 0) * 100,
          parseFloat(rule.occupancy_max || 1) * 100
        ]}
        onChange={(e, newValue) => onSliderChange(newValue)}
        valueLabelDisplay="auto"
        step={10}
        marks
        min={0}
        max={100}
        aria-label="Occupancy Range"
        size="small"
      />
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#8B7355' }}>
          {Math.round(parseFloat(rule.occupancy_min || 0) * 100)}% occupancy
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600, color: '#8B7355' }}>
          {Math.round(parseFloat(rule.occupancy_max || 1) * 100)}% occupancy
        </Typography>
      </Box>
    </Grid>
  );

  const renderPriceInput = (value, onChange, onBlur, disabled = false) => (
    <TextField
      label="Unit Price"
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onBlur={onBlur}
      InputProps={{
        startAdornment: <InputAdornment position="start">£</InputAdornment>,
      }}
      inputProps={{
        inputMode: 'decimal',
        pattern: '[0-9]*\\.?[0-9]*'
      }}
      disabled={disabled}
      fullWidth
      size="small"
    />
  );

  const renderBasePriceSection = (tariff) => (
    <StyledBasePriceSection>
      <Grid container spacing={2}>
        <Grid item xs={12} md={3}>
          <TextField
            label="Base Price"
            type="text"
            value={editingPrices[`tariff-${tariff.slug}`] !== undefined 
              ? editingPrices[`tariff-${tariff.slug}`] 
              : getDisplayPrice(tariff.unit_price || 0)}
            onChange={(e) => handleTariffPriceChange(tariff.slug, e.target.value)}
            onBlur={() => handleTariffPriceBlur(tariff.slug)}
            InputProps={{
              startAdornment: <InputAdornment position="start">£</InputAdornment>,
            }}
            inputProps={{
              inputMode: 'decimal',
              pattern: '[0-9]*\\.?[0-9]*'
            }}
            disabled={isUpdating}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Valid from"
            type="date"
            value={tariff.start ? tariff.start.split('T')[0] : ''}
            onChange={(e) => handleTariffDateChange(tariff.slug, 'start', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            label="Valid to"
            type="date"
            value={tariff.end ? tariff.end.split('T')[0] : ''}
            onChange={(e) => handleTariffDateChange(tariff.slug, 'end', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            fullWidth
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <TextField
            select
            label="Status"
            value={tariff.status || 'AC'}
            onChange={(e) => handleTariffStatusToggle(tariff.slug, e.target.value)}
            size="small"
            fullWidth
            SelectProps={{
              native: true,
            }}
          >
            <option value="AC">Active</option>
            <option value="IN">Inactive</option>
          </TextField>
        </Grid>
      </Grid>
      
      {/* Discrete VAT toggle in bottom right */}
      {renderDiscreteVATToggle()}
    </StyledBasePriceSection>
  );

  const renderActionButtons = (tariff) => {
    // Check if there are active rules to determine if "Add New Rule" should show
    const hasActiveRules = tariff.rules && tariff.rules.length > 0 && 
      tariff.rules.some(rule => rule.status === 'AC');

    return (
      <Box sx={{ 
        display: 'flex', 
        gap: 2, 
        justifyContent: 'center',
        mt: 2,
        mb: 2 
      }}>
        <Button
          variant="contained"
          onClick={() => handleSaveTariff(tariff.slug)}
          disabled={isUpdating || tariffSavingState[tariff.slug]}
          startIcon={tariffSavingState[tariff.slug] ? <CircularProgress size={20} /> : null}
          size="medium"
          sx={{
            background: '#F1C40F',
            color: '#5D4E37',
            fontWeight: 600,
            borderRadius: '50px',
            px: 3,
            '&:hover': {
              background: '#F4D03F',
            }
          }}
        >
          {tariffSavingState[tariff.slug] ? 'Saving...' : 'Save Changes'}
        </Button>
        
        {hasActiveRules && !showNewRuleForm[tariff.slug] && (!tariff.rules || tariff.rules.length < 3) && (
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            onClick={() => toggleNewRuleForm(tariff.slug)}
            size="medium"
            sx={{
              borderStyle: 'dashed',
              borderWidth: 2,
              borderColor: '#D4A574',
              color: '#D4A574',
              fontWeight: 600,
              borderRadius: '50px',
              px: 3,
              '&:hover': {
                backgroundColor: '#D4A57410',
                borderColor: '#F1C40F',
                color: '#F1C40F',
              },
            }}
          >
            Add New Rule
          </Button>
        )}
      </Box>
    );
  };

  const renderExistingRule = (tariff, rule, ruleIndex) => (
    <StyledRuleBox 
      key={rule.slug || ruleIndex}
      sx={{ 
        opacity: rule.status === 'IN' ? 0.5 : 1,
        border: rule.status === 'IN' ? '1px dashed #D4A574' : '1px solid #D4A574'
      }}
    >
      <Typography variant="body2" gutterBottom>
        Days of the week:
      </Typography>
      
      {renderDayCheckboxes(
        rule.days_of_week,
        (dayIndex) => handleDayToggle(tariff.slug, ruleIndex, dayIndex)
      )}

      <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
        Occupancy range and price:
      </Typography>
      <Grid container spacing={2} alignItems="center">
        {renderOccupancySlider(
          rule,
          (newValue) => handleSliderChange(tariff.slug, ruleIndex, newValue)
        )}
        <Grid item xs={12} sm={4}>
          {renderPriceInput(
            editingPrices[getPriceKey(tariff.slug, ruleIndex)] !== undefined 
              ? editingPrices[getPriceKey(tariff.slug, ruleIndex)] 
              : getDisplayPrice(rule.unit_price),
            (value) => handleInputChange(tariff.slug, ruleIndex, value),
            () => handlePriceBlur(tariff.slug, ruleIndex),
            rule.status === 'IN'
          )}
        </Grid>
      </Grid>
    </StyledRuleBox>
  );

  const renderNewRuleForm = (tariff) => (
    <StyledNewRuleBox>
      <Typography variant="subtitle2" gutterBottom>
        New Rule
      </Typography>

      <Typography variant="body2" gutterBottom>
        Days of the week:
      </Typography>
      {renderDayCheckboxes(
        newRules[tariff.slug]?.days_of_week,
        (dayIndex) => handleNewRuleDayToggle(tariff.slug, dayIndex)
      )}

      <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
        Occupancy range and price:
      </Typography>
      <Grid container spacing={2} alignItems="center">
        {renderOccupancySlider(
          newRules[tariff.slug] || { occupancy_min: 0, occupancy_max: 1 },
          (newValue) => {
            handleNewRuleChange(tariff.slug, 'occupancy_min', (newValue[0] / 100).toFixed(2));
            handleNewRuleChange(tariff.slug, 'occupancy_max', (newValue[1] / 100).toFixed(2));
          }
        )}
        <Grid item xs={12} sm={4}>
          {renderPriceInput(
            editingPrices[`new-${tariff.slug}`] !== undefined 
              ? editingPrices[`new-${tariff.slug}`] 
              : getDisplayPrice(newRules[tariff.slug]?.unit_price),
            (value) => handleNewRuleChange(tariff.slug, 'unit_price', value),
            () => handleNewRulePriceBlur(tariff.slug)
          )}
        </Grid>
      </Grid>

      <StyledActionButtons>
        <Button
          variant="outlined"
          onClick={() => toggleNewRuleForm(tariff.slug)}
          size="small"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={() => addTariffRule(tariff.slug)}
          disabled={isSavingRule}
          startIcon={isSavingRule ? <CircularProgress size={16} /> : null}
          size="small"
        >
          {isSavingRule ? 'Adding...' : 'Add Rule'}
        </Button>
      </StyledActionButtons>
    </StyledNewRuleBox>
  );

  const renderDynamicPricingSection = (tariff) => {
    // Show section only if there are active rules
    const hasActiveRules = tariff.rules && tariff.rules.length > 0 && 
      tariff.rules.some(rule => rule.status === 'AC');

    return (
      <StyledDynamicPricingContainer>
        <Collapse in={hasActiveRules} timeout={300}>
          <Box sx={{ mt: 3, mb: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#5D4E37', mb: 1 }}>
              Special pricing rules:
              {tariff.rules && (
                <Typography component="span" sx={{ ml: 1, color: '#8B7355', fontSize: '0.875rem', fontWeight: 600 }}>
                  ({tariff.rules.filter(rule => rule.status === 'AC').length}/{tariff.rules.length} active)
                </Typography>
              )}
            </Typography>
          </Box>

          {tariff.rules && tariff.rules.length > 0 ? (
            tariff.rules.map((rule, ruleIndex) => 
              renderExistingRule(tariff, rule, ruleIndex)
            )
          ) : (
            <Box sx={{ 
              textAlign: 'center', 
              py: 3, 
              color: '#8B7355',
              fontStyle: 'italic'
            }}>
              <Typography variant="body2">
                No special pricing rules defined for this tariff yet.
              </Typography>
            </Box>
          )}

          {/* New rule form if showing */}
          {showNewRuleForm[tariff.slug] && renderNewRuleForm(tariff)}
        </Collapse>
      </StyledDynamicPricingContainer>
    );
  };

  const renderTariffCard = (tariff) => (
    <StyledTariffCard key={tariff.slug}>
      <CardContent sx={{ pt: 3 }}>
        {renderBasePriceSection(tariff)}

        {/* Dynamic pricing toggle */}
        {renderDynamicPricingToggle(tariff)}

        {/* Dynamic pricing section */}
        {renderDynamicPricingSection(tariff)}

        {/* Action buttons */}
        {renderActionButtons(tariff)}
      </CardContent>
    </StyledTariffCard>
  );

  const renderContent = () => {
    if (isLoadingTariffs) {
      return renderLoadingState();
    }

    if (tariffs.length === 0) {
      return renderEmptyState();
    }

    return (
      <HoneyContainer>
        {renderHeader()}

        {tariffError && renderErrorAlert()}

        <Divider sx={{ my: 3, backgroundColor: '#D4A574', opacity: 0.3 }} />

        {tariffs.map(renderTariffCard)}
      </HoneyContainer>
    );
  };

  return renderContent();
};

export default PricingTabView;